import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { notification } from "antd";
import {
  initializeFCM,
  listenForMessages,
  parseFirebaseMessagePayload,
} from "../services/firebaseMessaging";
import { API_URL } from "constants/ApiConstant";
import { post } from "services/ApiService";
import { AUTH_TOKEN } from "constants/AuthConstant";
import { useGetNotification } from "NotificationContext";

const FirebaseContext = createContext(null);

export const useFirebase = () => useContext(FirebaseContext);

export const FirebaseProvider = ({ children }) => {
  const [firebaseToken, setFirebaseToken] = useState(null);
  const { refreshNotifications } = useGetNotification();

  const showForegroundMessage = useCallback((payload) => {
    const { title, body } = parseFirebaseMessagePayload(payload);

    if (!title && !body) {
      return;
    }

    notification.info({
      message: title,
      description: body || undefined,
      duration: 5,
      placement: "topRight",
    });
  }, []);

  useEffect(() => {
    const initFirebase = async () => {
      if (
        typeof Notification !== "undefined" &&
        Notification.permission === "granted"
      ) {
        const token = await initializeFCM();
        if (token) {
          setFirebaseToken(token);
        }
      }
    };

    initFirebase();

    const unsubscribe = listenForMessages((payload) => {
      showForegroundMessage(payload);

      if (localStorage.getItem(AUTH_TOKEN)) {
        refreshNotifications?.();
      }
    });

    return unsubscribe;
  }, [refreshNotifications, showForegroundMessage]);

  useEffect(() => {
    const handleFCMToken = async () => {
      const res = await post(API_URL.setFCMToken, { token: firebaseToken });
      if (res?.status === 200) {
        console.log("Firebase notification token set successfully");
      } else {
        console.log("Firebase notification token set failed");
      }
    };

    if (firebaseToken && localStorage.getItem(AUTH_TOKEN)) {
      handleFCMToken();
    }
  }, [firebaseToken]);

  const requestNotificationPermission = useCallback(async () => {
    if (typeof Notification === "undefined") {
      return null;
    }

    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      const token = await initializeFCM();
      if (token) {
        setFirebaseToken(token);
        console.log("FCM Token:", token);
      }
    }

    return null;
  }, []);

  useEffect(() => {
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "default"
    ) {
      requestNotificationPermission();
    }
  }, [requestNotificationPermission]);

  return (
    <FirebaseContext.Provider
      value={{ firebaseToken, requestNotificationPermission }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};
